import React, { useState, useEffect, useMemo, useCallback, useDeferredValue } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import '../../styles/ecommerce.css';

import UnifiedHeader from '../common/UnifiedHeader';
import StoreInSetup from '../common/StoreInSetup';
import UnifiedFooter from '../common/UnifiedFooter';
import UnifiedHeroBanner, { DEFAULT_UNIFIED_BANNERS } from '../common/UnifiedHeroBanner';
import EcommerceSidebar from './EcommerceSidebar';
import EcommerceProductCard from './EcommerceProductCard';
import EcommerceProductDetail from './EcommerceProductDetail';
import EcommerceCartDrawer from './EcommerceCartDrawer';
import EcommerceCheckoutModal from './EcommerceCheckoutModal';
import EcommerceAuthModal from './EcommerceAuthModal';
import EcommerceWhatsAppFloating from './EcommerceWhatsAppFloating';
import { AVAILABLE_COLORS, matchesProductColor, applyEcommerceTheme } from '../../utils/ecommerceHelper';
import { getFontFamilyCss } from '../../utils/fontHelper';
import { DEFAULT_BENEFITS_PRIMARY, DEFAULT_BENEFITS_SETTINGS, normalizeBenefitsList, resolvePilarFontSizeCss, resolvePilarFontWeightCss } from '../landing/BenefitsAdminPanel';
import { useWebStore } from '../../context/WebStoreContext';
import Swal from 'sweetalert2';

// Artículos de demostración en caso de que el backend no devuelva registros o esté en modo offline
const FALLBACK_ARTICLES = [
  {
    c_artic: 'RES-A4-75',
    d_artic: 'Resma Papel Multifunción A4 75g (500 hojas)',
    d_arti2: 'Papel alcalino ultra blanco para impresión láser e inkjet',
    c_rubro: '1',
    d_rubro: 'Papelería & Resmas',
    c_marca: 'CHAMEK',
    c_barra: '7791234567890',
    q_unxbu: 5,
    hasPublicPrice: true,
    i_preant: 7800.00,
    i_conta: 6450.00,
    variantes: [
      { id: 'v1', name: '75 grs (Estándar)' },
      { id: 'v2', name: '80 grs (Premium)' }
    ]
  },
  {
    c_artic: 'BOL-BIC-CRIS',
    d_artic: 'Bolígrafo Cristal Trazo Fino 0.8mm',
    d_arti2: 'Tinta indeleble fluida cuerpo hexagonal',
    c_rubro: '2',
    d_rubro: 'Escritura',
    c_marca: 'BIC',
    c_barra: '7799876543210',
    q_unxbu: 50,
    hasPublicPrice: false,
    i_conta: 520.00,
    variantes: [
      { id: 'v1', name: 'Azul', color: '#1e40af' },
      { id: 'v2', name: 'Negro', color: '#0f172a' },
      { id: 'v3', name: 'Rojo', color: '#dc2626' },
      { id: 'v4', name: 'Verde', color: '#16a34a' }
    ]
  },
  {
    c_artic: 'CUA-UNI-80',
    d_artic: 'Cuaderno Universitario Tapa Dura Rayado 80h',
    d_arti2: 'Espiral metálico continuo hojas microperforadas',
    c_rubro: '3',
    d_rubro: 'Cuadernos & Carpetas',
    c_marca: 'RIVADAVIA',
    c_barra: '7793344556677',
    q_unxbu: 10,
    hasPublicPrice: false,
    i_conta: 4890.00,
    variantes: [
      { id: 'v1', name: 'Rayado', color: '#3b82f6' },
      { id: 'v2', name: 'Cuadriculado', color: '#ef4444' },
      { id: 'v3', name: 'Liso', color: '#10b981' }
    ]
  },
  {
    c_artic: 'MAR-RES-FLUO',
    d_artic: 'Resaltador Pastel y Fluo Punta Biselada',
    d_arti2: 'Tinta base agua secado instantáneo no traspasa la hoja',
    c_rubro: '2',
    d_rubro: 'Escritura',
    c_marca: 'TRABI',
    c_barra: '7795566778899',
    q_unxbu: 12,
    hasPublicPrice: false,
    i_conta: 1150.00,
    variantes: [
      { id: 'v1', name: 'Amarillo', color: '#facc15' },
      { id: 'v2', name: 'Verde', color: '#4ade80' },
      { id: 'v3', name: 'Rosa Pastel', color: '#f472b6' },
      { id: 'v4', name: 'Celeste Pastel', color: '#38bdf8' }
    ]
  },
  {
    c_artic: 'CIN-EMB-48',
    d_artic: 'Cinta de Embalar Transparente 48mm x 50m',
    d_arti2: 'Adhesivo acrílico de alta resistencia y durabilidad',
    c_rubro: '4',
    d_rubro: 'Embalaje & Logística',
    c_marca: 'DOBLE A',
    c_barra: '7794455667788',
    q_unxbu: 36,
    hasPublicPrice: true,
    i_conta: 1850.00,
    variantes: [
      { id: 'v1', name: 'Transparente' },
      { id: 'v2', name: 'Marrón' }
    ]
  },
  {
    c_artic: 'BRO-ABR-10',
    d_artic: 'Abrochadora Metálica de Mesa Pinza 24/6',
    d_arti2: 'Cuerpo de acero reforzado capacidad hasta 30 hojas',
    c_rubro: '5',
    d_rubro: 'Insumos de Oficina',
    c_marca: 'MIT',
    c_barra: '7796677889900',
    q_unxbu: 1,
    hasPublicPrice: false,
    i_conta: 14200.00,
    variantes: [
      { id: 'v1', name: 'Negro' },
      { id: 'v2', name: 'Gris Metal' }
    ]
  }
];

const DEFAULT_BENEFITS = DEFAULT_BENEFITS_PRIMARY;

const PRICE_LISTS = [
  { id: 1, description: 'Lista 1 - Minorista' },
  { id: 2, description: 'Lista 2 - Mayorista (-15%)' },
  { id: 3, description: 'Lista 3 - Distribuidor (-25%)' }
];

const DEFAULT_STORE_CONFIG = {
  logoUrl: '/hiperpel-logo.png',
  logoText: 'HIPERPEL S.A.',
  footer: {
    companyName: 'HIPERPEL S.A.',
    tagline: 'Fabricantes de productos de cartón, envases, embalajes, descartables, gastronomía y librería. Venta mayorista y minorista con envíos a todo el país.',
    phone: '(011) 4247-6622',
    whatsapp: '5491142476622',
    email: 'ventas@hiperpel.com.ar',
    address: 'Vicente Damonte 1237 / 1247, Lanús Este (B1824ALG), Buenos Aires',
    scheduleMonFri: '08:00 a 18:00 hs',
    scheduleSat: '08:00 a 13:00 hs',
    websiteUrl: 'https://hiperpel.ar',
    instagram: 'https://instagram.com/hiperpel',
    facebook: 'https://facebook.com/hiperpel',
    twitter: '',
    linkedin: '',
    pinterest: '',
    copyrightText: `© ${new Date().getFullYear()} HIPERPEL S.A. - Todos los derechos reservados.`
  },
  whatsappFloating: {
    enabled: true,
    phone: '5491142476622',
    message: '¡Hola! Me comunico desde el catálogo online de HIPERPEL S.A. para hacer una consulta.',
    pillText: '¿Dudas? ¡Escribinos!',
    showPill: true
  },
  modules: {
    filterBrands: true,
    filterColors: true,
    filterSizes: true,
    filterPresentation: true,
    filterOffers: true,
    filterStock: true,
    filterSort: true,
    showCarousel: true,
    showHeroBanner: true,
    showServicesBar: true,
  }
};

const EcommerceStore = ({ user, onLogout }) => {
  // --- Estados de Datos ---
  const [articles, setArticles] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [pricesByArtic, setPricesByArtic] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // --- Estados de Simulación / Demo ---
  const [mockUser, setMockUser] = useState(null);

  // --- Contexto Global de Tienda Web (Cliente sincronizado en vivo) ---
  const webStoreCtx = useWebStore();
  const contextCustomer = webStoreCtx?.ecomCustomer;

  // --- Estado de Cliente eCommerce (Persistido en localStorage y conectado a puri000) ---
  const [ecomCustomer, setEcomCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_customer');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Mantener ecomCustomer sincronizado con el contexto global
  useEffect(() => {
    setEcomCustomer(contextCustomer || null);
    if (!contextCustomer) {
      setActivePriceList(1);
    }
  }, [contextCustomer]);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('register'); // 'register' | 'login'

  const [activePriceList, setActivePriceList] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_customer');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.n_lista) return Number(parsed.n_lista);
      }
    } catch {
      // ignore
    }
    return 1;
  });

  // Reaccionar automáticamente ante cambios en la lista de precios asignada al cliente
  useEffect(() => {
    const targetList = ecomCustomer?.n_lista || contextCustomer?.n_lista;
    if (targetList) {
      setActivePriceList(Number(targetList));
    }
  }, [ecomCustomer?.n_lista, contextCustomer?.n_lista]);

  const [publicPriceEnabled, setPublicPriceEnabled] = useState(true);

  // --- Estados de Filtros y Búsqueda ---
  const PAGE_CHUNK = 50;
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('q') || params.get('search') || '';
    } catch {
      return '';
    }
  });
  const deferredSearch = useDeferredValue(searchTerm);
  const [debouncedSearch, setDebouncedSearch] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('q') || params.get('search') || '';
    } catch {
      return '';
    }
  });
  const [selectedRubro, setSelectedRubro] = useState(null);
  const [onlyBestSellers, setOnlyBestSellers] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('mas_vendidos') === '1' || params.get('destacados') === '1';
    } catch {
      return false;
    }
  });
  const [selectedIdsFilter, setSelectedIdsFilter] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('ids') || '';
    } catch {
      return '';
    }
  });
  const [customFilterTitle, setCustomFilterTitle] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('titulo') || '';
    } catch {
      return '';
    }
  });

  const [selectedBrands, setSelectedBrands] = useState([]); // array de marcas seleccionadas
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [onlyOffers, setOnlyOffers] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('ofertas') === '1' || params.get('only_offers') === 'true';
    } catch {
      return false;
    }
  });

  const [sortBy, setSortBy] = useState('default');
  const [presentationFilter, setPresentationFilter] = useState('all'); // 'all' | 'unit' | 'bulk'
  const [selectedColors, setSelectedColors] = useState([]); // array de IDs de colores seleccionados
  const [selectedSizes, setSelectedSizes] = useState([]); // array de talles seleccionados
  const [selectedFamilia, setSelectedFamilia] = useState(null); // Subrubro / Familia activo
  const [familias, setFamilias] = useState([]); // Lista de familias/subrubros

  // Sincronizar filtros cuando cambia el query param en la URL (ej: clic en Barra Secundaria / Mega-Menú)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const queryParam = params.get('q') ?? params.get('search');
      const rubroParam = params.get('rubro') ?? params.get('c_rubro');
      const familiaParam = params.get('familia') ?? params.get('c_famil') ?? params.get('subrubro');
      const masVendidosParam = params.get('mas_vendidos') === '1' || params.get('destacados') === '1';
      const idsParam = params.get('ids') || '';
      const tituloParam = params.get('titulo') || '';
      const ofertasParam = params.get('ofertas') === '1' || params.get('only_offers') === 'true';

      setOnlyBestSellers(masVendidosParam);
      setSelectedIdsFilter(idsParam);
      setCustomFilterTitle(tituloParam);

      if (ofertasParam) {
        setOnlyOffers(true);
      }

      if (queryParam !== null && queryParam !== undefined) {
        setSearchTerm(queryParam);
        if (queryParam.trim() !== '') {
          setSelectedRubro(null);
          setSelectedFamilia(null);
        }
      } else if (masVendidosParam || idsParam || rubroParam || familiaParam) {
        setSearchTerm('');
      }

      if (rubroParam) {
        const cleanParam = rubroParam.trim().toLowerCase();
        if (rubros && rubros.length > 0) {
          const match = rubros.find(r =>
            String(r.c_rubro).trim().toLowerCase() === cleanParam ||
            (r.d_rubro && r.d_rubro.trim().toLowerCase() === cleanParam) ||
            (r.d_rubro && r.d_rubro.trim().toLowerCase().includes(cleanParam))
          );
          if (match) {
            setSelectedRubro(match.c_rubro);
          } else {
            setSelectedRubro(rubroParam.trim());
          }
        } else {
          setSelectedRubro(rubroParam.trim());
        }
      } else if (masVendidosParam || idsParam || (queryParam && queryParam.trim() !== '')) {
        setSelectedRubro(null);
      }

      if (familiaParam && familiaParam.trim() !== '') {
        setSelectedFamilia(familiaParam.trim());
      } else {
        setSelectedFamilia(null);
      }
    } catch (e) {
      console.warn('Error syncing search params:', e);
    }
  }, [location.search, rubros]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true); // Colapsar barra en desktop
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false); // Drawer de filtros en mobile
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('sigecom_ecom_view_mode') || 'grid';
    } catch {
      return 'grid';
    }
  });

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('sigecom_ecom_view_mode', mode);
    } catch (e) {
      console.error('Error saving viewMode', e);
    }
  };

  // --- Estados de Paginación Progresiva del Servidor ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalArticlesCount, setTotalArticlesCount] = useState(0);
  const [catalogTotalCount, setCatalogTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [rubrosMap, setRubrosMap] = useState({});

  // Debounce para la búsqueda en el servidor (350ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handlers para filtros multiselección (toggle)
  const handleToggleBrand = (brandId) => {
    if (!brandId) {
      setSelectedBrands([]);
      return;
    }
    setSelectedBrands(prev => 
      prev.includes(brandId) ? prev.filter(b => b !== brandId) : [...prev, brandId]
    );
  };

  const handleToggleColor = (colorId) => {
    if (!colorId) {
      setSelectedColors([]);
      return;
    }
    setSelectedColors(prev => 
      prev.includes(colorId) ? prev.filter(c => c !== colorId) : [...prev, colorId]
    );
  };

  const handleToggleSize = (size) => {
    if (!size) {
      setSelectedSizes([]);
      return;
    }
    setSelectedSizes(prev => 
      prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]
    );
  };

  // --- Estados de Carrito (Persistente en localStorage) ---
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  // --- Estados de Modales y Widgets ---
  const [selectedDetailProduct, setSelectedDetailProduct] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isWhatsAppVisible, setIsWhatsAppVisible] = useState(true);
  const { storeConfig: globalStoreConfig, landingConfig: globalLandingConfig } = useWebStore();
  const [storeConfig, setStoreConfig] = useState(() => {
    if (globalStoreConfig) {
      return {
        ...DEFAULT_STORE_CONFIG,
        ...globalStoreConfig,
        logoUrl: globalStoreConfig.logoUrl !== undefined ? globalStoreConfig.logoUrl : '',
        logoText: globalStoreConfig.logoText || globalStoreConfig.footer?.companyName || '',
        modules: {
          ...DEFAULT_STORE_CONFIG.modules,
          ...(globalStoreConfig.modules || {})
        },
        footer: {
          ...DEFAULT_STORE_CONFIG.footer,
          ...(globalStoreConfig.footer || {})
        }
      };
    }
    return {
      ...DEFAULT_STORE_CONFIG,
      logoUrl: '',
      logoText: ''
    };
  });
  const [landingConfig, setLandingConfig] = useState(globalLandingConfig || null);

  useEffect(() => {
    if (globalStoreConfig) {
      setStoreConfig(prev => ({
        ...DEFAULT_STORE_CONFIG,
        ...globalStoreConfig,
        logoUrl: globalStoreConfig.logoUrl !== undefined ? globalStoreConfig.logoUrl : prev.logoUrl,
        logoText: globalStoreConfig.logoText || globalStoreConfig.footer?.companyName || prev.logoText,
        modules: {
          ...DEFAULT_STORE_CONFIG.modules,
          ...(globalStoreConfig.modules || {})
        },
        footer: {
          ...DEFAULT_STORE_CONFIG.footer,
          ...(globalStoreConfig.footer || {})
        }
      }));
    }
  }, [globalStoreConfig]);

  useEffect(() => {
    if (globalLandingConfig) {
      setLandingConfig(globalLandingConfig);
    }
  }, [globalLandingConfig]);
  const urlParams = new URLSearchParams(window.location.search);
  const [storeCompanyId, setStoreCompanyId] = useState(() => urlParams.get('company') || '');

  const targetCompanyId = urlParams.get('company') || storeCompanyId || storeConfig?.company_id || landingConfig?.company_id || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 5 : null);

  // 0. Cargar configuración visual y comercial de la tienda y landing
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const explicitCompany = urlParams.get('company');
        const configOptions = explicitCompany ? {
          params: { company: explicitCompany, _t: Date.now() },
          headers: { 'X-Company-ID': String(explicitCompany) }
        } : {
          params: { _t: Date.now() }
        };
        const [res, resLanding] = await Promise.all([
          axios.get('/api/ecommerce/config/', configOptions),
          axios.get('/api/landing/config/', configOptions).catch(() => ({ data: null }))
        ]);
        if (resLanding && resLanding.data) {
          setLandingConfig(resLanding.data);
        }
        if (res.data) {
          const resolvedCompId = res.data.company_id ? Number(res.data.company_id) : 1;
          if (!urlParams.get('company')) {
            setStoreCompanyId(resolvedCompId);
          }
          setStoreConfig({
            ...DEFAULT_STORE_CONFIG,
            ...res.data,
            company_id: resolvedCompId,
            modules: {
              ...DEFAULT_STORE_CONFIG.modules,
              ...(res.data.modules || {})
            },
            footer: {
              ...DEFAULT_STORE_CONFIG.footer,
              ...(res.data.footer || {}),
              facebook: (res.data.footer?.facebook !== undefined && res.data.footer?.facebook !== '') ? res.data.footer.facebook : DEFAULT_STORE_CONFIG.footer.facebook,
              instagram: (res.data.footer?.instagram !== undefined && res.data.footer?.instagram !== '') ? res.data.footer.instagram : DEFAULT_STORE_CONFIG.footer.instagram,
              twitter: res.data.footer?.twitter || res.data.footer?.x || '',
              linkedin: res.data.footer?.linkedin || '',
              pinterest: res.data.footer?.pinterest || ''
            }
          });
          if (res.data.commercial && res.data.commercial.publicPricesEnabled !== undefined) {
            setPublicPriceEnabled(Boolean(res.data.commercial.publicPricesEnabled));
          }
          if (res.data.commercial && res.data.commercial.defaultPriceList !== undefined) {
            try {
              const savedCustomer = localStorage.getItem('sigecom_ecom_customer');
              const parsedCustomer = savedCustomer ? JSON.parse(savedCustomer) : null;
              if (!parsedCustomer || !parsedCustomer.n_lista) {
                setActivePriceList(Number(res.data.commercial.defaultPriceList) || 1);
              }
            } catch {
              setActivePriceList(Number(res.data.commercial.defaultPriceList) || 1);
            }
          }
          // Inyectar variables de color unificadas (dando prioridad a la paleta oficial de landing)
          const colorsToApply = resLanding?.data?.colors || resLanding?.data?.styles || res.data.colors || res.data.styles;
          if (colorsToApply) {
            applyEcommerceTheme(colorsToApply);
          }
        }
      } catch (e) {
        console.warn('Configuración por defecto en tienda:', e);
      }
    };
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Guardar carrito cada vez que cambia
  useEffect(() => {
    localStorage.setItem('sigecom_ecom_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // 1. Cargar lista de rubros ordenada desde la API de Django para la empresa activa de la tienda
  const fetchRubros = useCallback(async (companyId) => {
    const compToUse = companyId || targetCompanyId;
    try {
      const reqOptions = compToUse ? {
        params: { company: compToUse, _t: Date.now() },
        headers: { 'X-Company-ID': String(compToUse) }
      } : { params: { _t: Date.now() } };
      const resRubros = await axios.get('/api/rubros/', reqOptions);
      let loadedRubros = [];
      if (Array.isArray(resRubros.data) && resRubros.data.length > 0) {
        loadedRubros = resRubros.data.filter(r => {
          if (!r) return false;
          if (r.c_situa === 9 || Number(r.c_situa) === 9) return false;
          if (r.q_articulos !== undefined && Number(r.q_articulos) <= 0) return false;
          const name = String(r.d_rubro || '').trim().toLowerCase();
          if (name === 'productos viejos') return false;
          return true;
        });
      }

      const map = {};
      loadedRubros.forEach(r => {
        if (r.c_rubro !== undefined && r.c_rubro !== null) {
          const rubName = (r.d_rubro || '').trim();
          if (rubName && !rubName.toLowerCase().startsWith('rubro')) {
            map[String(r.c_rubro).trim()] = rubName;
          }
        }
      });
      setRubrosMap(map);

      if (loadedRubros.length > 0) {
        const sortedRubros = [...loadedRubros].sort((a, b) => {
          const nameA = (a.d_rubro || `Rubro ${a.c_rubro || ''}`).trim().toUpperCase();
          const nameB = (b.d_rubro || `Rubro ${b.c_rubro || ''}`).trim().toUpperCase();
          return nameA.localeCompare(nameB, 'es-AR');
        });
        setRubros(sortedRubros);

        // Si vino un parámetro ?rubro=... en la URL, resolver por código o por texto
        const rubroParam = urlParams.get('rubro') || urlParams.get('c_rubro');
        if (rubroParam) {
          const cleanParam = rubroParam.trim().toLowerCase();
          const match = sortedRubros.find(r => 
            String(r.c_rubro).trim() === cleanParam || 
            (r.d_rubro && r.d_rubro.trim().toLowerCase() === cleanParam) ||
            (r.d_rubro && r.d_rubro.trim().toLowerCase().includes(cleanParam))
          );
          if (match) {
            setSelectedRubro(match.c_rubro);
          }
        }
      } else {
        setRubros([]);
      }
    } catch (err) {
      console.warn('Error al cargar rubros desde la API:', err);
      setRubros([]);
    }
  }, [targetCompanyId]);

  useEffect(() => {
    fetchRubros(targetCompanyId);
  }, [targetCompanyId, fetchRubros]);

  // Cargar lista de familias / subrubros desde la API
  const fetchFamilias = useCallback(async (companyId) => {
    try {
      const params = new URLSearchParams();
      if (companyId) params.append('company', String(companyId));
      const reqHeaders = companyId ? { 'X-Company-ID': String(companyId) } : {};
      const res = await axios.get(`/api/familias/?${params.toString()}`, { headers: reqHeaders });
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setFamilias(data);
    } catch (err) {
      console.warn('Error al cargar familias desde la API:', err);
      setFamilias([]);
    }
  }, []);

  useEffect(() => {
    fetchFamilias(targetCompanyId);
  }, [targetCompanyId, fetchFamilias]);

  // 2. Cargar artículos de forma paginada (100 por lote) según rubro, subrubro/familia, búsqueda, stock, ofertas, marca, orden, talles, colores, más vendidos o IDs
  const loadArticles = useCallback(async (
    pageNumber = 1,
    isAppend = false,
    activeRubro = selectedRubro,
    activeFamilia = selectedFamilia,
    activeSearch = debouncedSearch,
    activeOnlyStock = onlyInStock,
    activeOnlyOffers = onlyOffers,
    activeBrands = selectedBrands,
    activeSort = sortBy,
    activeSizes = selectedSizes,
    activeColors = selectedColors,
    activeBestSellers = onlyBestSellers,
    activeIds = selectedIdsFilter
  ) => {
    if (isAppend) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const params = new URLSearchParams();
      params.append('page', String(pageNumber));
      params.append('page_size', String(PAGE_CHUNK));

      if (activeBestSellers) {
        const bsConfig = landingConfig?.bestSellersSection || storeConfig?.bestSellersSection || {};
        const featConfig = landingConfig?.featuredProductsSection || storeConfig?.featuredProductsSection || {};
        const bsMode = bsConfig.selectionMode || (bsConfig.selectedProducts?.length > 0 ? 'manual' : 'featured');
        if (bsMode === 'manual' && Array.isArray(bsConfig.selectedProducts) && bsConfig.selectedProducts.length > 0) {
          const codes = bsConfig.selectedProducts.map(p => p.c_artic || p.id).filter(Boolean);
          if (codes.length > 0) {
            params.append('ids', codes.join(','));
          }
        } else if (bsMode === 'rubros' && Array.isArray(bsConfig.selectedRubros) && bsConfig.selectedRubros.length > 0) {
          params.append('rubro', bsConfig.selectedRubros.join(','));
        } else if (bsMode === 'ofertas' || bsMode === 'offers') {
          params.append('only_offers', 'true');
        } else if (Array.isArray(featConfig.selectedProducts) && featConfig.selectedProducts.length > 0) {
          const codes = featConfig.selectedProducts.map(p => p.c_artic || p.id).filter(Boolean);
          if (codes.length > 0) {
            params.append('ids', codes.join(','));
          }
        } else if (featConfig.selectionMode === 'rubro' && featConfig.filterRubro) {
          params.append('rubro', String(featConfig.filterRubro).trim());
        }
      }

      if (activeIds && String(activeIds).trim() !== '') {
        params.set('ids', String(activeIds).trim());
      }

      if (activeRubro !== null && activeRubro !== undefined && String(activeRubro).trim() !== '') {
        params.append('rubro', String(activeRubro).trim());
      }
      if (activeFamilia !== null && activeFamilia !== undefined && String(activeFamilia).trim() !== '') {
        params.append('familia', String(activeFamilia).trim());
      }
      if (activeSearch && activeSearch.trim() !== '') {
        params.append('search', activeSearch.trim());
      }
      if (activeOnlyStock) {
        params.append('in_stock', 'true');
      }
      if (activeOnlyOffers) {
        params.append('only_offers', 'true');
      }
      if (Array.isArray(activeBrands) && activeBrands.length > 0) {
        params.append('brand', activeBrands.join(','));
      }
      if (Array.isArray(activeSizes) && activeSizes.length > 0) {
        params.append('sizes', activeSizes.join(','));
      }
      if (Array.isArray(activeColors) && activeColors.length > 0) {
        params.append('colors', activeColors.join(','));
      }
      if (activeSort === 'name_asc' || activeSort === 'name_desc') {
        params.append('sort', activeSort);
      }
      if (targetCompanyId) {
        params.append('company', String(targetCompanyId));
      }

      const reqHeaders = targetCompanyId ? { 'X-Company-ID': String(targetCompanyId) } : {};
      const res = await axios.get(`/api/articulos/?${params.toString()}`, {
        headers: reqHeaders
      });
      const data = res.data;
      const rawResults = Array.isArray(data) ? data : (data.results || []);
      const count = (data && typeof data.count === 'number') ? data.count : rawResults.length;
      const hasNext = Boolean(data && data.next);

      // Enriquecer cada artículo con el nombre de su rubro
      const enriched = rawResults.map(art => {
        const rubKey = art.c_rubro !== undefined && art.c_rubro !== null ? String(art.c_rubro).trim() : '';
        const rubName = rubrosMap[rubKey] || (art.d_rubro && !art.d_rubro.toLowerCase().startsWith('rubro') ? art.d_rubro : '');
        return {
          ...art,
          c_rubro: rubKey,
          d_rubro: rubName
        };
      });

      if (isAppend) {
        setArticles(prev => [...prev, ...enriched]);
      } else {
        setArticles(enriched);
        if (!activeRubro && !activeFamilia && !activeBestSellers && !activeIds && (!activeSearch || !activeSearch.trim()) && !activeOnlyStock && !activeOnlyOffers && (!activeBrands || activeBrands.length === 0) && (!activeSizes || activeSizes.length === 0) && (!activeColors || activeColors.length === 0)) {
          setCatalogTotalCount(count);
        }
      }

      setCurrentPage(pageNumber);
      setTotalArticlesCount(count);
      setHasNextPage(hasNext);
    } catch (err) {
      console.warn('Error al cargar artículos desde la API:', err);
      if (!isAppend) {
        setArticles(prev => (prev.length === 0 ? [] : prev));
        setHasNextPage(false);
      }
    } finally {
      if (isAppend) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [selectedRubro, selectedFamilia, debouncedSearch, onlyInStock, onlyOffers, selectedBrands, sortBy, rubrosMap, targetCompanyId, selectedSizes, selectedColors, onlyBestSellers, selectedIdsFilter, landingConfig?.bestSellersSection, landingConfig?.featuredProductsSection, storeConfig?.bestSellersSection, storeConfig?.featuredProductsSection]);

  // Cargar artículos en página 1 cuando cambia cualquier filtro de servidor
  useEffect(() => {
    loadArticles(1, false, selectedRubro, selectedFamilia, debouncedSearch, onlyInStock, onlyOffers, selectedBrands, sortBy, selectedSizes, selectedColors, onlyBestSellers, selectedIdsFilter);
  }, [selectedRubro, selectedFamilia, debouncedSearch, onlyInStock, onlyOffers, selectedBrands, sortBy, selectedSizes, selectedColors, onlyBestSellers, selectedIdsFilter, loadArticles]);

  const handleLoadMore = () => {
    if (!isLoadingMore && hasNextPage) {
      loadArticles(currentPage + 1, true, selectedRubro, selectedFamilia, debouncedSearch, onlyInStock, onlyOffers, selectedBrands, sortBy, selectedSizes, selectedColors, onlyBestSellers, selectedIdsFilter);
    }
  };

  // Cargar precios según la lista activa
  useEffect(() => {
    if (activePriceList) {
      const pUrl = targetCompanyId 
        ? `/api/precios/?n_lista=${activePriceList}&company=${targetCompanyId}`
        : `/api/precios/?n_lista=${activePriceList}`;
      const pHeaders = targetCompanyId ? { 'X-Company-ID': String(targetCompanyId) } : {};
      axios.get(pUrl, {
        headers: pHeaders
      })
        .then(res => {
          const map = {};
          if (Array.isArray(res.data)) {
            res.data.forEach(p => {
              map[p.c_artic] = p.i_conta || p.i_lista || p.precio;
            });
          }
          setPricesByArtic(map);
        })
        .catch(() => {
          // Si no responde la API de precios, simular multiplicadores de lista para la maqueta
          const map = {};
          const multiplier = activePriceList === 2 ? 0.85 : activePriceList === 3 ? 0.75 : 1.0;
          articles.forEach(a => {
            const base = a.i_conta || a.i_lista || 1000;
            map[a.c_artic] = Math.round(base * multiplier);
          });
          setPricesByArtic(map);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePriceList, articles]);

  // Artículos dentro del alcance del rubro seleccionado (o todos si no hay rubro filtrado)
  const rubroScopeArticles = useMemo(() => {
    if (selectedRubro === null || selectedRubro === undefined || String(selectedRubro).trim() === '') {
      return articles;
    }
    const cleanRubro = String(selectedRubro).trim();
    return articles.filter(a => String(a.c_rubro || '').trim() === cleanRubro);
  }, [articles, selectedRubro]);

  // Lista de Marcas únicas para filtros (dinámica según el rubro seleccionado o catálogo general)
  const availableBrands = useMemo(() => {
    const brandsMap = new Map();
    rubroScopeArticles.forEach(a => {
      const code = (a.c_marca || '').trim();
      const label = (a.d_marca || a.c_marca || '').trim();
      if (code || label) {
        const key = code || label;
        if (!brandsMap.has(key)) {
          brandsMap.set(key, label || code);
        }
      }
    });
    return Array.from(brandsMap.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rubroScopeArticles]);

  // Lista de Talles únicos para filtros (dinámica: muestra talles de rubros al inicio, y se ajusta según el rubro)
  const availableSizes = useMemo(() => {
    const isGlobal = selectedRubro === null || selectedRubro === undefined || String(selectedRubro).trim() === '';
    const sizesSet = new Set();

    if (isGlobal) {
      // Al inicio (vista global), extraer todos los posibles talles configurados en los rubros
      rubros.forEach(r => {
        if (r.l_talle) {
          for (let i = 1; i <= 12; i++) {
            const nom = r[`d_talle${i}`];
            if (nom && String(nom).trim()) {
              sizesSet.add(String(nom).trim());
            }
          }
        }
      });
      // También agregar talles de los artículos cargados
      articles.forEach(a => {
        if (Array.isArray(a.talles)) {
          a.talles.forEach(t => {
            if (t.nombre && String(t.nombre).trim()) {
              sizesSet.add(String(t.nombre).trim());
            }
          });
        }
      });
    } else {
      // Cuando se selecciona un rubro específico, solo mostrar los talles reales de los artículos de ese rubro
      rubroScopeArticles.forEach(a => {
        if (Array.isArray(a.talles)) {
          a.talles.forEach(t => {
            if (t.nombre && String(t.nombre).trim()) {
              sizesSet.add(String(t.nombre).trim());
            }
          });
        }
      });
    }

    if (sizesSet.size === 0) return [];

    const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
    return Array.from(sizesSet).sort((a, b) => {
      const idxA = SIZE_ORDER.indexOf(String(a).toUpperCase());
      const idxB = SIZE_ORDER.indexOf(String(b).toUpperCase());
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return String(a).localeCompare(String(b));
    });
  }, [rubroScopeArticles, selectedRubro, rubros, articles]);

  // Lista de Colores/Variedades disponibles (dinámica: muestra todos inicialmente, y se ajusta según el rubro)
  const availableColors = useMemo(() => {
    // Si no hay rubro seleccionado (pantalla inicial / "Todos los productos"), mostrar TODOS los colores
    if (selectedRubro === null || selectedRubro === undefined || String(selectedRubro).trim() === '') {
      return AVAILABLE_COLORS;
    }

    const matchedColorIds = new Set();
    rubroScopeArticles.forEach(product => {
      AVAILABLE_COLORS.forEach(color => {
        if (matchesProductColor(product, color.id)) {
          matchedColorIds.add(color.id);
        }
      });
    });
    if (matchedColorIds.size === 0) return [];
    return AVAILABLE_COLORS.filter(c => matchedColorIds.has(c.id));
  }, [rubroScopeArticles, selectedRubro]);

  // Limpiar filtros activos que ya no existan al cambiar de rubro
  useEffect(() => {
    if (selectedColors.length > 0) {
      const validColors = selectedColors.filter(cId => availableColors.some(ac => ac.id === cId));
      if (validColors.length !== selectedColors.length) {
        setSelectedColors(validColors);
      }
    }
    if (selectedSizes.length > 0) {
      const validSizes = selectedSizes.filter(s => availableSizes.includes(s));
      if (validSizes.length !== selectedSizes.length) {
        setSelectedSizes(validSizes);
      }
    }
    if (selectedBrands.length > 0) {
      const validBrands = selectedBrands.filter(bId => availableBrands.some(ab => ab.id === bId));
      if (validBrands.length !== selectedBrands.length) {
        setSelectedBrands(validBrands);
      }
    }
  }, [selectedRubro, availableColors, availableSizes, availableBrands, selectedColors, selectedSizes, selectedBrands]);

  // Conteo de artículos por rubro
  const rubroCounts = useMemo(() => {
    const counts = {};
    rubros.forEach(r => {
      if (r.c_rubro !== undefined && r.c_rubro !== null) {
        const key = String(r.c_rubro).trim();
        counts[key] = r.q_articulos !== undefined ? r.q_articulos : 0;
      }
    });
    return counts;
  }, [rubros]);

  // Familias / Subrubros pertenecientes al rubro seleccionado (dinámico)
  const currentRubroFamilias = useMemo(() => {
    if (!selectedRubro) return [];
    const cleanRubro = String(selectedRubro).trim().toLowerCase();

    // 1. Familias obtenidas de la API mfam000
    const apiFamilias = (familias || [])
      .filter(f => 
        String(f.c_rubro || '').trim().toLowerCase() === cleanRubro &&
        f.c_famil && String(f.c_famil).trim() !== '' &&
        f.d_famil && String(f.d_famil).trim() !== ''
      )
      .map(f => ({
        c_famil: String(f.c_famil).trim(),
        d_famil: String(f.d_famil).trim(),
        c_rubro: String(f.c_rubro).trim()
      }));

    // 2. Familias inferidas de los artículos cargados del catálogo (solo si tienen d_famil)
    const articleFamMap = {};
    (articles || []).forEach(art => {
      if (String(art.c_rubro || '').trim().toLowerCase() === cleanRubro && art.c_famil) {
        const fKey = String(art.c_famil).trim();
        const fName = (art.d_famil || '').trim();
        if (fKey && fName && !articleFamMap[fKey]) {
          articleFamMap[fKey] = {
            c_famil: fKey,
            d_famil: fName,
            c_rubro: art.c_rubro
          };
        }
      }
    });

    const combined = [...apiFamilias];
    Object.values(articleFamMap).forEach(af => {
      if (!combined.some(f => String(f.c_famil).trim() === af.c_famil)) {
        combined.push(af);
      }
    });

    return combined
      .filter(f => f.d_famil && f.d_famil.trim() !== '')
      .sort((a, b) => (a.d_famil || '').localeCompare(b.d_famil || '', 'es-AR'));
  }, [selectedRubro, familias, articles]);

  // Usuario o Cliente Activo del eCommerce (puri000 o simulación)
  const activeCustomer = ecomCustomer || mockUser || null;

  // Determinar si los precios están habilitados públicamente o por login
  const arePublicPricesAllowed = Boolean(
    storeConfig?.commercial?.publicPricesEnabled !== undefined
      ? storeConfig.commercial.publicPricesEnabled
      : publicPriceEnabled
  );

  // Determinar si el usuario actual o visitante puede ver precios
  const canUserSeePrices = Boolean(activeCustomer || arePublicPricesAllowed);

  // Obtener precio efectivo de un artículo (si tiene i_preant > 0, ese es el precio de oferta a cobrar)
  const getProductPrice = React.useCallback((product) => {
    const offerPrice = Number(product.i_preant ?? product.I_PREANT ?? 0);
    if (offerPrice > 0) {
      return offerPrice;
    }
    if (pricesByArtic[product.c_artic]) {
      return pricesByArtic[product.c_artic];
    }
    const base = product.i_conta || product.i_lista || 1500;
    const mult = activePriceList === 2 ? 0.85 : activePriceList === 3 ? 0.75 : 1.0;
    return Math.round(base * mult);
  }, [pricesByArtic, activePriceList]);

  // Obtener precio regular de lista (para mostrar tachado si hay oferta)
  const getRegularPrice = React.useCallback((product) => {
    if (pricesByArtic[product.c_artic]) {
      return pricesByArtic[product.c_artic];
    }
    const base = product.i_conta || product.i_lista || 1500;
    const mult = activePriceList === 2 ? 0.85 : activePriceList === 3 ? 0.75 : 1.0;
    return Math.round(base * mult);
  }, [pricesByArtic, activePriceList]);

  // Determinar si un artículo individual puede mostrar precio
  const canShowArticlePrice = (product) => {
    if (activeCustomer) return true;
    if (arePublicPricesAllowed) return true;
    if (publicPriceEnabled && product?.hasPublicPrice) return true;
    return false;
  };

  // Filtrado y ordenamiento dinámico del catálogo
  const filteredArticles = useMemo(() => {
    // Normalizador de acentos y minúsculas para búsquedas flexibles
    const cleanStr = (str) =>
      (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

    return articles.filter(product => {
      // 1. Filtro de búsqueda multi-palabra y flexible (ej: "plato de aluminio" -> "plato" + "aluminio", o código/ID "100")
      if (deferredSearch && deferredSearch.trim()) {
        const queryTerm = cleanStr(deferredSearch).trim();
        const itemCode = cleanStr(product.c_artic);
        const itemCodeUnpadded = itemCode.replace(/^0+/, '');
        const queryUnpadded = queryTerm.replace(/^0+/, '');

        // Coincidencia directa por código / ID
        const isDirectCodeMatch = itemCode === queryTerm || 
                                  (queryUnpadded && itemCodeUnpadded === queryUnpadded) ||
                                  itemCode.includes(queryTerm) ||
                                  cleanStr(product.c_barra).includes(queryTerm);

        if (!isDirectCodeMatch) {
          const rawTerms = queryTerm.split(/\s+/).filter(Boolean);
          const meaningfulTerms = rawTerms.filter(t => t.length > 2 || !['de', 'el', 'la', 'en', 'un', 'al'].includes(t));
          const tokensToMatch = meaningfulTerms.length > 0 ? meaningfulTerms : rawTerms;

          const itemDesc = cleanStr(product.d_artic);
          const itemDesc2 = cleanStr(product.d_arti2);
          const itemBrand = cleanStr(product.c_marca);
          const itemBar = cleanStr(product.c_barra);
          const fullItemText = `${itemCode} ${itemCodeUnpadded} ${itemDesc} ${itemDesc2} ${itemBrand} ${itemBar}`;

          const matchesAllTokens = tokensToMatch.every(t => fullItemText.includes(t));
          if (!matchesAllTokens) return false;
        }
      }

      // 2. Filtro de Rubro
      if (selectedRubro !== null && String(product.c_rubro || '').trim() !== String(selectedRubro).trim()) {
        return false;
      }

      // 2.1. Filtro de Subrubro / Familia
      if (selectedFamilia !== null && String(product.c_famil || '').trim() !== String(selectedFamilia).trim()) {
        return false;
      }

      // 3. Filtro de Marcas (Multiselección OR)
      if (selectedBrands.length > 0) {
        const matchesAnyBrand = selectedBrands.some(brandTarget => {
          const targetUpper = String(brandTarget).trim().toUpperCase();
          const artMarca = String(product.c_marca || '').trim().toUpperCase();
          const artMarcaDesc = String(product.d_marca || '').trim().toUpperCase();
          const artDesc = String(product.d_artic || '').trim().toUpperCase();
          return (artMarca === targetUpper) || 
                 (artMarcaDesc === targetUpper) ||
                 (artDesc.includes(targetUpper));
        });
        if (!matchesAnyBrand) return false;
      }

      // 4. Filtro Solo con Stock
      if (onlyInStock) {
        const directStock = Number(product.q_stock || 0);
        const hasTallesStock = Array.isArray(product.talles) && product.talles.some(t => Number(t.stock || 0) > 0);
        if (directStock <= 0 && !hasTallesStock) {
          return false;
        }
      }

      // 4.1. Filtro Solo Ofertas
      if (onlyOffers) {
        const previousPrice = Number(product.i_preant ?? product.I_PREANT ?? 0);
        if (previousPrice <= 0) {
          return false;
        }
      }

      // 5. Filtro de Presentación (Unidad vs Bulto)
      if (presentationFilter === 'unit') {
        const isBulk = (product.q_unxbu && Number(product.q_unxbu) > 1) || 
                       (product.d_artic && (
                         product.d_artic.toUpperCase().includes('BULTO') || 
                         product.d_artic.toUpperCase().includes('PACK X') ||
                         product.d_artic.toUpperCase().includes('X 50') ||
                         product.d_artic.toUpperCase().includes('X 100') ||
                         product.d_artic.toUpperCase().includes('X 1000') ||
                         product.d_artic.toUpperCase().includes('X 5000')
                       ));
        if (isBulk) return false;
      } else if (presentationFilter === 'bulk') {
        const isBulk = (product.q_unxbu && Number(product.q_unxbu) > 1) || 
                       (product.d_artic && (
                         product.d_artic.toUpperCase().includes('BULTO') || 
                         product.d_artic.toUpperCase().includes('PACK') ||
                         product.d_artic.toUpperCase().includes('CAJA') ||
                         product.d_artic.toUpperCase().includes('X 50') ||
                         product.d_artic.toUpperCase().includes('X 100') ||
                         product.d_artic.toUpperCase().includes('X 1000') ||
                         product.d_artic.toUpperCase().includes('X 5000')
                       ));
        if (!isBulk) return false;
      }

      // 6. Filtro de Colores / Variedades (Multiselección OR)
      if (selectedColors.length > 0) {
        const matchesAnyColor = selectedColors.some(colorId => matchesProductColor(product, colorId));
        if (!matchesAnyColor) return false;
      }

      // 7. Filtro de Talles / Medidas (Multiselección OR)
      if (selectedSizes.length > 0) {
        const matchesAnySize = selectedSizes.some(sizeTarget => {
          const targetUpper = String(sizeTarget).trim().toUpperCase();
          const matchOfficial = Array.isArray(product.talles) && product.talles.some(t =>
            String(t.nombre).trim().toUpperCase() === targetUpper &&
            (!onlyInStock || Number(t.stock || 0) > 0)
          );
          const descUpper = String(product.d_artic || '').toUpperCase();
          const matchInDesc = descUpper.includes(`TALLE ${targetUpper}`) ||
                              descUpper.includes(`T.${targetUpper}`) ||
                              descUpper.includes(`T ${targetUpper}`) ||
                              descUpper.includes(` ${targetUpper} `) ||
                              descUpper.endsWith(` ${targetUpper}`);
          return matchOfficial || matchInDesc;
        });
        if (!matchesAnySize) return false;
      }

      return true;
    }).sort((a, b) => {
      if (sortBy === 'name_asc') return (a.d_artic || '').localeCompare(b.d_artic || '');
      if (sortBy === 'name_desc') return (b.d_artic || '').localeCompare(a.d_artic || '');
      if (sortBy === 'price_asc') return getProductPrice(a) - getProductPrice(b);
      if (sortBy === 'price_desc') return getProductPrice(b) - getProductPrice(a);
      return 0;
    });
  }, [articles, deferredSearch, selectedRubro, selectedFamilia, selectedBrands, onlyInStock, onlyOffers, sortBy, presentationFilter, selectedColors, selectedSizes, getProductPrice]);

  // Artículos actualmente visibles tras filtros locales
  const visibleArticles = filteredArticles;



  // Función auxiliar para clave única de ítem en carrito (artículo + variante + talle)
  const getItemCartKey = (item) => {
    const variantId = item.selectedVariant?.id || 'std';
    const sizeId = item.selectedSize?.id || item.selectedSize?.nombre || 'nosize';
    return `${item.c_artic}_${variantId}_${sizeId}`;
  };

  // Manejadores del Carrito con validación estricta de stock y requerimiento de autenticación
  const handleAddToCart = (itemToAdd) => {
    if (!activeCustomer) {
      Swal.fire({
        icon: 'info',
        title: 'Iniciá sesión para comprar',
        text: 'Para agregar productos al carrito y realizar pedidos necesitás registrarte o iniciar sesión con tu cuenta de cliente.',
        showCancelButton: true,
        confirmButtonColor: '#0d6efd',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Identificarme / Registrarme',
        cancelButtonText: 'Seguir mirando'
      }).then((result) => {
        if (result.isConfirmed) {
          handleOpenAuthModal('register');
        }
      });
      return;
    }

    const maxStock = itemToAdd.maxStock !== undefined 
      ? Number(itemToAdd.maxStock)
      : (itemToAdd.selectedSize && itemToAdd.selectedSize.stock !== undefined)
        ? Number(itemToAdd.selectedSize.stock)
        : Number(itemToAdd.q_stock ?? itemToAdd.stock ?? 0);

    const itemKey = getItemCartKey(itemToAdd);
    let wasAdded = false;

    setCartItems(prev => {
      const existingIndex = prev.findIndex(item => getItemCartKey(item) === itemKey);
      const currentQty = existingIndex > -1 ? prev[existingIndex].quantity : 0;
      const desiredQty = currentQty + itemToAdd.quantity;

      if (maxStock > 0 && desiredQty > maxStock) {
        const availableToAdd = maxStock - currentQty;
        Swal.fire({
          icon: 'warning',
          title: 'Stock insuficiente',
          text: availableToAdd > 0 
            ? `Solo podés agregar ${availableToAdd} unidad(es) más. Stock total disponible: ${maxStock}.`
            : `Ya alcanzaste la cantidad máxima disponible (${maxStock} unidades) en tu carrito.`,
          confirmButtonColor: '#0d6efd',
          confirmButtonText: 'Entendido'
        });
        return prev;
      }

      wasAdded = true;
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: desiredQty,
          unitPrice: itemToAdd.unitPrice,
          maxStock: maxStock > 0 ? maxStock : updated[existingIndex].maxStock
        };
        return updated;
      }
      return [...prev, { ...itemToAdd, maxStock: maxStock > 0 ? maxStock : undefined }];
    });

    if (wasAdded) {
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: '¡Producto agregado al carrito!',
        showConfirmButton: false,
        timer: 1500
      });
    }
  };

  const handleUpdateQuantity = (itemKey, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(itemKey);
      return;
    }
    setCartItems(prev => prev.map(item => {
      const key = getItemCartKey(item);
      if (key === itemKey) {
        const maxStock = item.maxStock !== undefined
          ? Number(item.maxStock)
          : (item.selectedSize && item.selectedSize.stock !== undefined)
            ? Number(item.selectedSize.stock)
            : Number(item.q_stock ?? item.stock ?? 0);

        if (maxStock > 0 && newQuantity > maxStock) {
          Swal.fire({
            toast: true,
            position: 'top',
            icon: 'warning',
            title: `Máximo disponible: ${maxStock} unidades`,
            showConfirmButton: false,
            timer: 2000
          });
          return { ...item, quantity: maxStock };
        }
        return { ...item, quantity: newQuantity };
      }
      return item;
    }));
  };

  const handleRemoveItem = (itemKey) => {
    setCartItems(prev => prev.filter(item => getItemCartKey(item) !== itemKey));
  };

  const handleClearCart = () => {
    Swal.fire({
      title: '¿Vaciar carrito?',
      text: 'Se eliminarán todos los productos agregados.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, vaciar',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (result.isConfirmed) {
        setCartItems([]);
      }
    });
  };

  const handleOpenDetail = (product) => {
    setSelectedDetailProduct(product);
    setIsDetailOpen(true);
  };

  const handleOpenAuthModal = (mode = 'register') => {
    setAuthModalMode(mode);
    setIsAuthModalOpen(true);
  };

  const handleCustomerLogin = (customerData) => {
    setEcomCustomer(customerData);
    if (webStoreCtx?.setEcomCustomer) {
      webStoreCtx.setEcomCustomer(customerData);
    } else {
      localStorage.setItem('sigecom_ecom_customer', JSON.stringify(customerData));
      localStorage.setItem('sigecom_ecom_last_activity', Date.now().toString());
    }
    if (customerData?.n_lista) {
      setActivePriceList(Number(customerData.n_lista));
    }
  };

  const handleLogout = () => {
    setEcomCustomer(null);
    setMockUser(null);
    setActivePriceList(1);
    if (webStoreCtx?.logoutCustomer) {
      webStoreCtx.logoutCustomer(true);
    } else {
      if (webStoreCtx?.setEcomCustomer) {
        webStoreCtx.setEcomCustomer(null);
      }
      localStorage.removeItem('sigecom_ecom_customer');
      localStorage.removeItem('sigecom_ecom_last_activity');
      Swal.fire({
        title: 'Sesión cerrada',
        text: 'Has salido de tu cuenta de cliente.',
        icon: 'info',
        timer: 1500,
        showConfirmButton: false
      });
    }
  };

  const totalCartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalCartPrice = cartItems.reduce((sum, item) => sum + (Number(item.unitPrice || 0) * item.quantity), 0);

  const activeListLabel = activeCustomer?.nombre_lista || PRICE_LISTS.find(pl => pl.id === activePriceList)?.description || `Lista ${activePriceList}`;

  // Conteo de filtros activos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedRubro !== null) count++;
    if (selectedFamilia !== null) count++;
    if (onlyBestSellers) count++;
    if (selectedIdsFilter) count++;
    count += selectedBrands.length;
    count += selectedColors.length;
    count += selectedSizes.length;
    if (presentationFilter !== 'all') count++;
    if (onlyInStock) count++;
    if (onlyOffers) count++;
    return count;
  }, [selectedRubro, selectedFamilia, onlyBestSellers, selectedIdsFilter, selectedBrands, selectedColors, selectedSizes, presentationFilter, onlyInStock, onlyOffers]);

  const handleSelectRubro = (rubroCode) => {
    setSelectedRubro(rubroCode);
    setSelectedFamilia(null);
    if (rubroCode !== null) {
      setOnlyBestSellers(false);
      setSelectedIdsFilter('');
    }
  };

  const handleResetAllFilters = () => {
    setSelectedRubro(null);
    setSelectedFamilia(null);
    setOnlyBestSellers(false);
    setSelectedIdsFilter('');
    setCustomFilterTitle('');
    setSelectedBrands([]);
    setSelectedColors([]);
    setSelectedSizes([]);
    setOnlyInStock(false);
    setOnlyOffers(false);
    setSortBy('default');
    setPresentationFilter('all');
    setSearchTerm('');
  };

  const isInSetup = Boolean(
    storeConfig?.is_in_setup ||
    landingConfig?.is_in_setup ||
    storeConfig?.status === 'en_habilitacion' ||
    landingConfig?.status === 'en_habilitacion' ||
    storeConfig?.status === 'no_encontrada' ||
    landingConfig?.status === 'no_encontrada'
  );

  if (isInSetup) {
    return <StoreInSetup storeConfig={{ ...landingConfig, ...storeConfig }} />;
  }

  return (
    <div className="ecommerce-container webstore-site-wrapper" style={{ backgroundColor: 'var(--ecom-bg, #f8fafc)', color: 'var(--ecom-text, #1e293b)' }}>
      
      {/* 1. Cabecera Navbar Unificada en toda la Web */}
      <UnifiedHeader
        activePage="tienda"
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        cartCount={totalCartCount}
        cartTotal={totalCartPrice}
        currentUser={activeCustomer}
        onOpenCart={() => setIsCartOpen(true)}
        onLogout={handleLogout}
        onAuthClick={handleOpenAuthModal}
        priceListInfo={activeListLabel}
        canSeePrices={canUserSeePrices}
        topBarConfig={landingConfig?.topBar}
        storeConfig={storeConfig}
        rubros={rubros}
        onSelectRubro={(rubroCode) => {
          handleSelectRubro(rubroCode);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      {/* 2. HERO CAROUSEL BANNER UNIFICADO (Idéntico a la Página Web) */}
      {storeConfig?.modules?.showCarousel !== false && (
        <section className="py-3" style={{ backgroundColor: 'var(--ecom-bg, #f8fafc)' }}>
          <div className={(landingConfig?.carouselSettings || storeConfig?.carouselSettings)?.widthMode === 'fullwidth' ? 'container-fluid px-0' : 'container'}>
            <UnifiedHeroBanner
              banners={landingConfig?.heroBanners || landingConfig?.banners || storeConfig?.heroBanners || storeConfig?.banners || DEFAULT_UNIFIED_BANNERS}
              carouselSettings={landingConfig?.carouselSettings || storeConfig?.carouselSettings}
            />
          </div>
        </section>
      )}

      {/* 3. Contenido Principal */}
      <main className="container-fluid px-3 px-lg-4 py-4 flex-grow-1">
        
        {/* Barra de Beneficios (Mismos pilares configurados en la Web) */}
        {(() => {
          const storeBenefits = normalizeBenefitsList(landingConfig?.benefits || storeConfig?.benefits, DEFAULT_BENEFITS);
          if (!storeBenefits.length) return null;
          const barSettings = { ...DEFAULT_BENEFITS_SETTINGS, ...(landingConfig?.benefitsSettings || storeConfig?.benefitsSettings || {}) };
          const barFontCss = getFontFamilyCss(barSettings?.fontFamily);
          const line1FontCss = getFontFamilyCss(barSettings?.line1FontFamily || barSettings?.fontFamily);
          const line2FontCss = getFontFamilyCss(barSettings?.line2FontFamily || barSettings?.fontFamily);
          const line3FontCss = getFontFamilyCss(barSettings?.line3FontFamily || barSettings?.fontFamily);
          return (
            <div className="row g-3 mb-4" style={{ ...(barFontCss ? { fontFamily: barFontCss } : {}), ...(barSettings?.backgroundColor ? { backgroundColor: barSettings.backgroundColor, padding: "10px", borderRadius: "14px" } : {}) }}>
              {storeBenefits.map((b, idx) => (
                <div key={b.id || idx} className="col-12 col-sm-6 col-xl-3">
                  <div 
                    className="card shadow-xs rounded-4 h-100 p-3 d-flex flex-row align-items-center gap-3 border"
                    style={{ 
                      backgroundColor: b.bgColor || b.backgroundColor || barSettings?.cardBackgroundColor || 'var(--ecom-card-bg, #ffffff)', 
                      borderColor: 'var(--ecom-border, #e2e8f0)',
                      borderRadius: 'var(--ecom-radius, 12px)'
                    }}
                  >
                    <div 
                      className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '48px', height: '48px', fontSize: '1.35rem' }}
                    >
                      <i className={`bi ${b.icon || 'bi-check-circle'}`}></i>
                    </div>
                    <div>
                      <h6
                        className="mb-0.5"
                        style={{
                          ...(line1FontCss ? { fontFamily: line1FontCss } : {}),
                          color: b.titleColor || barSettings?.line1Color || '#1e293b',
                          fontSize: resolvePilarFontSizeCss(b.titleFontSize || barSettings?.line1FontSize, '0.95rem'),
                          fontWeight: resolvePilarFontWeightCss(b.titleFontWeight || barSettings?.line1FontWeight, 700),
                          lineHeight: 1.25
                        }}
                      >
                        {b.title}
                      </h6>
                      {b.description && (
                        <small
                          className="d-block lh-sm"
                          style={{
                            ...(line2FontCss ? { fontFamily: line2FontCss } : {}),
                            color: b.descColor || barSettings?.line2Color || '#64748b',
                            fontSize: resolvePilarFontSizeCss(b.descFontSize || barSettings?.line2FontSize, '0.84rem'),
                            fontWeight: resolvePilarFontWeightCss(b.descFontWeight || barSettings?.line2FontWeight, 400)
                          }}
                        >
                          {b.description}
                        </small>
                      )}
                      {(b.extraText || b.line3 || b.subtitle) && (
                        <small
                          className="d-block mt-1 lh-sm"
                          style={{
                            ...(line3FontCss ? { fontFamily: line3FontCss } : {}),
                            color: b.line3Color || barSettings?.line3Color || 'var(--ecom-primary, #0d6efd)',
                            fontSize: resolvePilarFontSizeCss(b.line3FontSize || barSettings?.line3FontSize, '0.75rem'),
                            fontWeight: resolvePilarFontWeightCss(b.line3FontWeight || barSettings?.line3FontWeight, 500)
                          }}
                        >
                          {b.extraText || b.line3 || b.subtitle}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Barra Rápida de Filtros para Mobile (< 992px) */}
        <div className="d-flex d-lg-none align-items-center justify-content-between gap-2 mb-3 bg-white p-3 rounded-4 border shadow-sm">
          <button
            type="button"
            className="btn btn-outline-primary rounded-pill px-3 py-2 fw-semibold d-flex align-items-center gap-2 shadow-sm"
            onClick={() => setIsMobileFilterOpen(true)}
          >
            <i className="bi bi-funnel-fill"></i>
            <span>Filtros</span>
            {activeFiltersCount > 0 && (
              <span className="badge bg-primary text-white rounded-pill ms-1">{activeFiltersCount}</span>
            )}
          </button>

          {storeConfig?.modules?.filterSort !== false && (
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted d-none d-sm-inline">Ordenar:</span>
              <select
                className="form-select form-select-sm rounded-pill"
                style={{ maxWidth: '160px' }}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default">Relevancia</option>
                <option value="name_asc">Nombre (A - Z)</option>
                <option value="name_desc">Nombre (Z - A)</option>
                <option value="price_asc">Menor Precio</option>
                <option value="price_desc">Mayor Precio</option>
              </select>
            </div>
          )}
        </div>

        {/* Grilla Principal con Filtros Laterales */}
        <div className="row g-4">
          
          {/* Barra Lateral de Filtros (Desktop: d-none d-lg-block si isSidebarVisible) */}
          {isSidebarVisible && (
            <div className="col-12 col-lg-3 d-none d-lg-block">
              <EcommerceSidebar
                rubros={rubros}
                selectedRubro={selectedRubro}
                onSelectRubro={handleSelectRubro}
                familias={currentRubroFamilias}
                selectedFamilia={selectedFamilia}
                onSelectFamilia={setSelectedFamilia}
                rubroCounts={rubroCounts}
                brands={availableBrands}
                selectedBrands={selectedBrands}
                onToggleBrand={handleToggleBrand}
                sortBy={sortBy}
                setSortBy={setSortBy}
                onlyInStock={onlyInStock}
                setOnlyInStock={setOnlyInStock}
                onlyOffers={onlyOffers}
                setOnlyOffers={setOnlyOffers}
                presentationFilter={presentationFilter}
                setPresentationFilter={setPresentationFilter}
                availableColors={availableColors}
                selectedColors={selectedColors}
                onToggleColor={handleToggleColor}
                availableSizes={availableSizes}
                selectedSizes={selectedSizes}
                onToggleSize={handleToggleSize}
                totalProductsCount={catalogTotalCount || totalArticlesCount}
                onResetFilters={handleResetAllFilters}
                onCollapse={() => setIsSidebarVisible(false)}
                filterModules={storeConfig?.modules}
              />
            </div>
          )}

          {/* Listado de Productos */}
          <div className={`col-12 ${isSidebarVisible ? 'col-lg-9' : 'col-lg-12'}`}>
            
            {/* Encabezado del Listado con Contador y Botón Discreto para reabrir filtros si están ocultos */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 bg-white p-3 rounded-4 border shadow-sm">
              <div className="d-flex flex-wrap align-items-center gap-2">
                {/* Botón discreto '>' para reabrir los filtros cuando están ocultos */}
                {!isSidebarVisible && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light border rounded-pill px-3 py-1 d-none d-lg-inline-flex align-items-center gap-2 text-dark fw-semibold shadow-sm me-1"
                    style={{ fontSize: '0.82rem' }}
                    onClick={() => setIsSidebarVisible(true)}
                    title="Mostrar panel de filtros (>)"
                  >
                    <i className="bi bi-chevron-right text-primary fw-bold" style={{ fontSize: '0.8rem' }}></i>
                    <span>Filtros</span>
                    {activeFiltersCount > 0 && (
                      <span className="badge bg-primary text-white rounded-pill ms-1">{activeFiltersCount}</span>
                    )}
                  </button>
                )}

                <span className="fw-bold text-dark fs-6 ms-1">
                  {totalArticlesCount > 0 ? (
                    <>
                      {articles.length < totalArticlesCount ? (
                        <>Mostrando <strong>{filteredArticles.length}</strong> de <strong>{totalArticlesCount}</strong> artículos</>
                      ) : (
                        <>{filteredArticles.length} {filteredArticles.length === 1 ? 'artículo encontrado' : 'artículos encontrados'}</>
                      )}
                    </>
                  ) : (
                    isLoading ? 'Buscando artículos...' : '0 artículos encontrados'
                  )}
                </span>
              </div>

              <div className="d-flex align-items-center gap-2">
                {!canUserSeePrices && (
                  <div className="small text-muted d-none d-sm-flex align-items-center gap-1 me-1">
                    <i className="bi bi-shield-lock text-warning"></i>
                    <span>Precios mayoristas bloqueados</span>
                  </div>
                )}

                {/* Switch de Vista: Grilla / Lista */}
                <div className="btn-group btn-group-sm border rounded-pill p-0.5 bg-light shadow-xs" role="group" aria-label="Modo de visualización">
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill px-3 py-1 d-inline-flex align-items-center gap-2 fw-semibold transition-all ${
                      viewMode === 'grid'
                        ? 'btn-white bg-white text-primary shadow-xs border'
                        : 'btn-light text-muted border-0'
                    }`}
                    onClick={() => handleViewModeChange('grid')}
                    title="Vista en Cuadrícula (Grilla)"
                  >
                    <i className="bi bi-grid-fill"></i>
                    <span className="d-none d-md-inline" style={{ fontSize: '0.78rem' }}>Grilla</span>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill px-3 py-1 d-inline-flex align-items-center gap-2 fw-semibold transition-all ${
                      viewMode === 'list'
                        ? 'btn-white bg-white text-primary shadow-xs border'
                        : 'btn-light text-muted border-0'
                    }`}
                    onClick={() => handleViewModeChange('list')}
                    title="Vista en Lista"
                  >
                    <i className="bi bi-view-list"></i>
                    <span className="d-none d-md-inline" style={{ fontSize: '0.78rem' }}>Lista</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Chips de Filtros Activos con eliminación rápida en un clic */}
            {activeFiltersCount > 0 && (
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 px-3 bg-white rounded-3 border shadow-sm">
                <span className="small text-muted fw-semibold me-1">
                  <i className="bi bi-funnel me-1"></i>Filtros activos:
                </span>
                {onlyBestSellers && (
                  <span className="badge bg-warning-subtle text-dark border border-warning-subtle rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <i className="bi bi-fire text-danger"></i>
                    <span><strong>{landingConfig?.bestSellersSection?.title || '¡Los Más Vendidos!'}</strong></span>
                    <i className="bi bi-x-circle-fill text-muted hover-danger cursor-pointer" onClick={() => setOnlyBestSellers(false)}></i>
                  </span>
                )}
                {selectedIdsFilter && (
                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <i className="bi bi-stars"></i>
                    <span>Selección: <strong>{customFilterTitle || 'Artículos Destacados'}</strong></span>
                    <i className="bi bi-x-circle-fill text-primary hover-danger cursor-pointer" onClick={() => { setSelectedIdsFilter(''); setCustomFilterTitle(''); }}></i>
                  </span>
                )}
                {selectedRubro !== null && (
                  <span className="badge bg-light text-dark border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <span>Rubro: <strong>{rubros.find(r => String(r.c_rubro).trim() === String(selectedRubro).trim())?.d_rubro || `Rubro ${selectedRubro}`}</strong></span>
                    <i className="bi bi-x-circle-fill text-muted hover-danger cursor-pointer" onClick={() => handleSelectRubro(null)}></i>
                  </span>
                )}
                {selectedFamilia !== null && (
                  <span className="badge bg-light text-dark border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <span>Subrubro: <strong>{currentRubroFamilias.find(f => String(f.c_famil).trim() === String(selectedFamilia).trim())?.d_famil || `Subrubro ${selectedFamilia}`}</strong></span>
                    <i className="bi bi-x-circle-fill text-muted hover-danger cursor-pointer" onClick={() => setSelectedFamilia(null)}></i>
                  </span>
                )}
                {selectedColors.map(colorId => (
                  <span key={`col_${colorId}`} className="badge bg-light text-dark border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <span>Color: <strong>{AVAILABLE_COLORS.find(c => c.id === colorId)?.name || colorId}</strong></span>
                    <i className="bi bi-x-circle-fill text-muted hover-danger cursor-pointer" onClick={() => handleToggleColor(colorId)}></i>
                  </span>
                ))}
                {selectedSizes.map(size => (
                  <span key={`sz_${size}`} className="badge bg-light text-dark border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <span>Talle: <strong>{size}</strong></span>
                    <i className="bi bi-x-circle-fill text-muted hover-danger cursor-pointer" onClick={() => handleToggleSize(size)}></i>
                  </span>
                ))}
                {selectedBrands.map(brandId => (
                  <span key={`br_${brandId}`} className="badge bg-light text-dark border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <span>Marca: <strong>{availableBrands.find(b => b.id === brandId)?.label || brandId}</strong></span>
                    <i className="bi bi-x-circle-fill text-muted hover-danger cursor-pointer" onClick={() => handleToggleBrand(brandId)}></i>
                  </span>
                ))}
                {presentationFilter !== 'all' && (
                  <span className="badge bg-light text-dark border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <span>{presentationFilter === 'unit' ? 'Por Unidad' : 'Bultos / Packs'}</span>
                    <i className="bi bi-x-circle-fill text-muted hover-danger cursor-pointer" onClick={() => setPresentationFilter('all')}></i>
                  </span>
                )}
                {onlyInStock && (
                  <span className="badge bg-light text-dark border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <span>Solo con stock</span>
                    <i className="bi bi-x-circle-fill text-muted hover-danger cursor-pointer" onClick={() => setOnlyInStock(false)}></i>
                  </span>
                )}
                {onlyOffers && (
                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 small">
                    <i className="bi bi-tag-fill"></i>
                    <span>Solo Ofertas</span>
                    <i className="bi bi-x-circle-fill text-danger hover-danger cursor-pointer" onClick={() => setOnlyOffers(false)}></i>
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger text-decoration-none p-0 small fw-semibold ms-auto"
                  onClick={handleResetAllFilters}
                >
                  Limpiar todos
                </button>
              </div>
            )}

            {/* Grid / Lista de Productos */}
            {isLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Cargando catálogo...</span>
                </div>
                <p className="text-muted">Cargando artículos...</p>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="bg-white rounded-4 border p-5 text-center shadow-sm">
                <i className="bi bi-search text-muted display-4 mb-3 d-block"></i>
                <h5 className="fw-bold text-dark">No se encontraron artículos</h5>
                <p className="text-muted small mb-3">
                  Probá modificando los filtros de búsqueda o categoría seleccionada.
                </p>
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill px-4"
                  onClick={handleResetAllFilters}
                >
                  Restablecer Filtros
                </button>
              </div>
            ) : (
              <>
                {viewMode === 'list' ? (
                  <div className="d-flex flex-column gap-3">
                    {visibleArticles.map(product => {
                      const effectivePrice = getProductPrice(product);
                      const regularPrice = getRegularPrice(product);
                      const canSeePrice = canShowArticlePrice(product);

                      return (
                        <EcommerceProductCard
                          key={product.c_artic}
                          product={product}
                          canSeePrice={canSeePrice}
                          price={effectivePrice}
                          regularPrice={regularPrice}
                          priceListLabel={canUserSeePrices ? activeListLabel : null}
                          selectedColorFilter={selectedColors}
                          selectedSizeFilter={selectedSizes}
                          isLoggedIn={Boolean(activeCustomer)}
                          viewMode="list"
                          onAddToCart={handleAddToCart}
                          onOpenDetail={handleOpenDetail}
                          onLoginClick={() => handleOpenAuthModal('register')}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className={`row ${isSidebarVisible ? 'row-cols-1 row-cols-sm-2 row-cols-md-2 row-cols-lg-3 row-cols-xl-3' : 'row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-4 row-cols-xxl-5'} g-3`}>
                    {visibleArticles.map(product => {
                      const effectivePrice = getProductPrice(product);
                      const regularPrice = getRegularPrice(product);
                      const canSeePrice = canShowArticlePrice(product);

                      return (
                        <div key={product.c_artic} className="col">
                          <EcommerceProductCard
                            product={product}
                            canSeePrice={canSeePrice}
                            price={effectivePrice}
                            regularPrice={regularPrice}
                            priceListLabel={canUserSeePrices ? activeListLabel : null}
                            selectedColorFilter={selectedColors}
                            selectedSizeFilter={selectedSizes}
                            isLoggedIn={Boolean(activeCustomer)}
                            viewMode="grid"
                            onAddToCart={handleAddToCart}
                            onOpenDetail={handleOpenDetail}
                            onLoginClick={() => handleOpenAuthModal('register')}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Botones de Carga Progresiva / Paginación del Servidor */}
                {(hasNextPage || isLoadingMore) && (
                  <div className="text-center my-4 py-4 bg-white rounded-4 border shadow-sm p-4">
                    <div className="d-flex flex-wrap align-items-center justify-content-center gap-3">
                      <button
                        type="button"
                        className="btn btn-primary rounded-pill px-4 py-2 shadow-sm fw-bold d-flex align-items-center gap-2"
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                      >
                        {isLoadingMore ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span>Cargando 100 más...</span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-arrow-down-circle fs-5"></i>
                            <span>Cargar 100 artículos más</span>
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-muted small mt-2 mb-0">
                      Mostrando <strong>{articles.length}</strong> de <strong>{totalArticlesCount}</strong> artículos disponibles
                    </p>
                  </div>
                )}

                {!hasNextPage && !isLoadingMore && articles.length > 0 && totalArticlesCount > 0 && (
                  <div className="text-center my-4 py-3 bg-light rounded-4 border text-muted small">
                    <i className="bi bi-check2-circle text-success me-1 fs-5 align-middle"></i>
                    Has visto todos los artículos disponibles ({articles.length} de {totalArticlesCount})
                  </div>
                )}
              </>
            )}

          </div>

        </div>

      </main>

      {/* 4. Drawer de Filtros para Mobile (< 992px) */}
      <div className={`ecom-filter-drawer ${isMobileFilterOpen ? 'open' : ''}`}>
        <div className="ecom-filter-drawer-header">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-funnel-fill text-primary fs-5"></i>
            <h6 className="fw-bold mb-0 text-dark">Filtros de Catálogo</h6>
            {activeFiltersCount > 0 && (
              <span className="badge bg-primary text-white rounded-pill">{activeFiltersCount}</span>
            )}
          </div>
          <button
            type="button"
            className="ecom-drawer-close-btn"
            onClick={() => setIsMobileFilterOpen(false)}
            title="Cerrar filtros"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <div className="ecom-filter-drawer-body">
          <EcommerceSidebar
            rubros={rubros}
            selectedRubro={selectedRubro}
            onSelectRubro={handleSelectRubro}
            familias={currentRubroFamilias}
            selectedFamilia={selectedFamilia}
            onSelectFamilia={setSelectedFamilia}
            rubroCounts={rubroCounts}
            brands={availableBrands}
            selectedBrands={selectedBrands}
            onToggleBrand={handleToggleBrand}
            sortBy={sortBy}
            setSortBy={setSortBy}
            onlyInStock={onlyInStock}
            setOnlyInStock={setOnlyInStock}
            onlyOffers={onlyOffers}
            setOnlyOffers={setOnlyOffers}
            presentationFilter={presentationFilter}
            setPresentationFilter={setPresentationFilter}
            availableColors={availableColors}
            selectedColors={selectedColors}
            onToggleColor={handleToggleColor}
            availableSizes={availableSizes}
            selectedSizes={selectedSizes}
            onToggleSize={handleToggleSize}
            totalProductsCount={catalogTotalCount || totalArticlesCount}
            onResetFilters={handleResetAllFilters}
            filterModules={storeConfig?.modules}
          />
        </div>
        <div className="ecom-filter-drawer-footer d-flex gap-2">
          {activeFiltersCount > 0 && (
            <button
              type="button"
              className="btn btn-outline-danger rounded-pill px-3 fw-semibold"
              onClick={handleResetAllFilters}
            >
              Limpiar
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary flex-grow-1 rounded-pill py-2 fw-bold shadow-sm"
            onClick={() => setIsMobileFilterOpen(false)}
          >
            Ver {filteredArticles.length} {filteredArticles.length === 1 ? 'artículo' : 'artículos'}
          </button>
        </div>
      </div>
      <div
        className={`ecom-filter-backdrop ${isMobileFilterOpen ? 'open' : ''}`}
        onClick={() => setIsMobileFilterOpen(false)}
      />

      {/* 5. Modal de Detalle de Producto */}
      <EcommerceProductDetail
        show={isDetailOpen}
        onHide={() => setIsDetailOpen(false)}
        product={selectedDetailProduct}
        canSeePrice={selectedDetailProduct ? canShowArticlePrice(selectedDetailProduct) : false}
        price={selectedDetailProduct ? getProductPrice(selectedDetailProduct) : 0}
        regularPrice={selectedDetailProduct ? getRegularPrice(selectedDetailProduct) : 0}
        priceListLabel={canUserSeePrices ? activeListLabel : null}
        isLoggedIn={Boolean(activeCustomer)}
        onAddToCart={handleAddToCart}
        onLoginClick={() => handleOpenAuthModal('register')}
        allArticles={articles}
        onSelectProduct={(p) => setSelectedDetailProduct(p)}
        storeConfig={storeConfig}
        getPriceForArticle={getProductPrice}
      />

      {/* 6. Carrito Deslizable (Cart Drawer) */}
      <EcommerceCartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onClearCart={handleClearCart}
        onCheckout={() => {
          if (!activeCustomer) {
            setIsCartOpen(false);
            handleOpenAuthModal('register');
            Swal.fire({
              title: '¡Identificación requerida!',
              text: 'Para poder finalizar la compra y confirmar tu pedido, por favor registrate o ingresá con tu correo.',
              icon: 'info',
              confirmButtonColor: '#0d6efd',
              confirmButtonText: 'Registrarme / Ingresar'
            });
            return;
          }
          setIsCartOpen(false);
          setIsCheckoutOpen(true);
        }}
        canSeePrices={canUserSeePrices}
        onLoginClick={() => handleOpenAuthModal('register')}
      />

      {/* 7. Modal de Checkout */}
      <EcommerceCheckoutModal
        show={isCheckoutOpen}
        onHide={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        canSeePrices={canUserSeePrices}
        currentUser={activeCustomer}
        whatsappSalesNumber={storeConfig?.commercial?.whatsappOrderNumber || storeConfig?.footer?.whatsapp || '5491142476622'}
        orderMessageTemplate={storeConfig?.commercial?.orderMessageTemplate}
        companyName={storeConfig?.footer?.companyName}
        storeConfig={storeConfig}
        companyId={targetCompanyId}
        onRequireAuth={() => {
          setIsCheckoutOpen(false);
          handleOpenAuthModal('register');
        }}
        onOrderCompleted={() => {
          setCartItems([]);
        }}
      />

      {/* 8. Modal de Autenticación / Registro de Cliente eCommerce (PURI000) */}
      <EcommerceAuthModal
        show={isAuthModalOpen}
        onHide={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
        onSuccess={handleCustomerLogin}
      />

      {/* 7. Footer Unificado del Ecommerce con datos oficiales o personalizados */}
      <UnifiedFooter
        storeConfig={storeConfig}
        landingConfig={landingConfig}
        rubros={rubros}
      />

      {/* 8. Botón Flotante de Acceso Rápido a WhatsApp (Atención al Cliente) con botón de cierre */}
      <EcommerceWhatsAppFloating
        isVisible={isWhatsAppVisible && storeConfig?.whatsappFloating?.enabled !== false}
        phoneNumber={storeConfig?.whatsappFloating?.phone || storeConfig?.footer?.whatsapp || '5491142476622'}
        defaultMessage={storeConfig?.whatsappFloating?.message || `¡Hola! Me comunico desde el catálogo online de ${storeConfig?.footer?.companyName || 'la empresa'} para hacer una consulta.`}
        pillText={storeConfig?.whatsappFloating?.pillText || '¿Dudas? ¡Escribinos!'}
        showPill={storeConfig?.whatsappFloating?.showPill !== false}
        onClose={() => setIsWhatsAppVisible(false)}
      />

    </div>
  );
};

export default EcommerceStore;
